import { Component } from "react";

import { AlertTriangle, RefreshCw } from "lucide-react";

import {
	Button,
	ButtonGroup,
	Card,
	CodeBox,
	Container,
	Description,
	ErrorDetails,
	ErrorIconWrapper,
	ExtraInfo,
	Footer,
	FooterItem,
	FooterText,
	FooterTitle,
	Main,
	Title,
} from "./ErrorBoundary.styles";

class ErrorBoundary extends Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	componentDidCatch(error, errorInfo) {
		console.error("Error caught by boundary:", error, errorInfo);

		const isChunkLoadError =
			error?.name === "ChunkLoadError" ||
			error?.message?.includes("Failed to fetch dynamically imported module") ||
			error?.message?.includes("Loading chunk") ||
			error?.message?.includes("Loading CSS chunk");

		if (isChunkLoadError) {
			const reloadKey = "chunk_reload_attempted";
			const lastReload = sessionStorage.getItem(reloadKey);
			const now = Date.now();

			if (!lastReload || now - Number(lastReload) > 10000) {
				sessionStorage.setItem(reloadKey, String(now));
				window.location.reload();
			}
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				<Container>
					<Main>
						<Card>
							<ErrorIconWrapper>
								<AlertTriangle size={40} style={{ color: "#6f57e9" }} />
							</ErrorIconWrapper>

							<Title>Oops! Something went wrong</Title>
							<Description>
								We encountered an unexpected error while processing your cargo
								shipment data. Don't worry, your shipment information is safe.
							</Description>

							<ErrorDetails>
								<h3
									style={{
										fontWeight: "600",
										color: "#111827",
										marginBottom: "0.5rem",
									}}
								>
									Error Details
								</h3>
								<CodeBox>
									{this.state.error?.message || "Unknown error occurred"}
								</CodeBox>
								<h3
									style={{
										fontWeight: "600",
										color: "#111827",
										marginBottom: "0.5rem",
										marginTop: "1rem",
									}}
								>
									URL
								</h3>
								<CodeBox>{window.location.href}</CodeBox>
							</ErrorDetails>

							<ButtonGroup>
								<Button primary onClick={() => window.location.reload()}>
									<RefreshCw size={20} />
									Reload Application
								</Button>
							</ButtonGroup>

							<Footer>
								<FooterItem>
									<FooterTitle>Need Help?</FooterTitle>
									<FooterText>cargosupport@shiprocket.com</FooterText>
									<FooterText>999-070-22-33</FooterText>
								</FooterItem>
								<FooterItem>
									<FooterTitle>Error Code</FooterTitle>
									<FooterText>SC-{Date.now().toString().slice(-6)}</FooterText>
								</FooterItem>
								<FooterItem>
									<FooterTitle>Status</FooterTitle>
									<FooterText color="#ea580c">Under Investigation</FooterText>
								</FooterItem>
							</Footer>

							<ExtraInfo>
								<p>
									This error has been automatically reported to our development
									team.
								</p>
								<p>
									We're working to resolve the issue as quickly as possible.
								</p>
							</ExtraInfo>
						</Card>
					</Main>
				</Container>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
